import { Random } from '@educraft/base';
import { C } from './c';
import { PageGen, PdfPen, PdfPoint, PdfRect, PdfSize } from '../pdf/pdf';

export const D1: string[] = ['↑', '→', '↓', '←'];
export const DX: number[] = [0, 1, 0, -1];
export const DY: number[] = [-1, 0, 1, 0];

export function drawMaze(
  g: PageGen,
  maze: boolean[][],
  rect: PdfRect,
  pen: PdfPen,
  ...spaces: C[]
): void {
  const width = maze.length;
  const height = maze[0].length;
  const hash = new Set<string>();
  const size = new PdfSize(rect.width / (width - 1), rect.height / (height - 1));

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (maze[x][y]) {
        const key = new C(x, y).toHash();
        hash.add(key);
      }
    }
  }

  for (const space of spaces) {
    const key = space.toHash();
    hash.delete(key);
  }

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const key = `${x},${y}`;
      if (hash.has(key)) {
        const r = new PdfRect(
          rect.x + x * size.width,
          rect.y + y * size.height,
          size.width,
          size.height
        );
        const p0 = new PdfPoint(r.left, r.top);

        for (let i = 0; i < DX.length; i++) {
          const nx = x + DX[i];
          const ny = y + DY[i];
          const neighborKey = `${nx},${ny}`;

          if (hash.has(neighborKey)) {
            const p1 = new PdfPoint(p0.x + DX[i] * size.width, p0.y + DY[i] * size.height);
            g.drawLine(pen, p0, p1);
          }
        }
      }
    }
  }
}

export function generateMaze(random: Random, width: number, height: number): boolean[][] {
  width |= 1;
  height |= 1;

  const maze: boolean[][] = Array.from({ length: width }, () => Array(height).fill(true));
  const stack: C[] = [];

  const startX = (width / 2) | 1;
  const startY = 1;
  maze[startX][startY] = false;
  stack.push(new C(startX, startY));

  while (stack.length > 0) {
    const current = stack[stack.length - 1];
    const { x, y } = current;
    let moved = false;

    const directions = [0, 1, 2, 3];
    for (let i = directions.length - 1; i > 0; i--) {
      const j = Math.floor(random.nextDouble() * (i + 1));
      [directions[i], directions[j]] = [directions[j], directions[i]];
    }

    for (const dir of directions) {
      const nx = x + DX[dir] * 2;
      const ny = y + DY[dir] * 2;

      if (nx >= 0 && ny >= 0 && nx < width && ny < height && maze[nx][ny]) {
        maze[x + DX[dir]][y + DY[dir]] = false;
        maze[nx][ny] = false;
        stack.push(new C(nx, ny));
        moved = true;
        break;
      }
    }

    if (!moved) {
      stack.pop();
    }
  }

  return maze;
}

export function findSolution(maze: boolean[][], start1: C, end1: C): C[] {
  const width = maze.length;
  const height = maze[0].length;

  const start = new C(start1.x | 1, start1.y | 1);
  const end = new C(end1.x | 1, end1.y | 1);

  const queue: C[] = [];
  const parent = new Map<string, C>();
  const visited = new Set<string>();

  queue.push(start);
  visited.add(start.toHash());
  parent.set(start.toHash(), new C(-1, -1));

  while (queue.length > 0) {
    const current = queue.shift()!;

    if (current.x === end.x && current.y === end.y) {
      const path: C[] = [];
      let step: C = current;

      while (step.x !== -1 && step.y !== -1) {
        path.push(step);
        const stepKey = step.toHash();
        step = parent.get(stepKey) || new C(-1, -1);
      }

      return path.reverse();
    }

    for (let i = 0; i < DX.length; i++) {
      const nx = current.x + DX[i];
      const ny = current.y + DY[i];
      const neighborKey = `${nx},${ny}`;

      if (
        nx >= 0 &&
        ny >= 0 &&
        nx < width &&
        ny < height &&
        !maze[nx][ny] &&
        !visited.has(neighborKey)
      ) {
        visited.add(neighborKey);
        parent.set(neighborKey, current);
        queue.push(new C(nx, ny));
      }
    }
  }

  return [];
}

export function isUsed(maze: boolean[][], x: number, y: number): boolean {
  const width = maze.length;
  const height = maze[0].length;

  if (x < 0 || y < 0 || x >= width + 2 || y >= height) return false;
  return maze[x][y];
}
